import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateSupplierRosterCache } from '../services/supplierRosterCache';
import { supabase } from '../services/supabase';
import { useAuth } from './useAuth';

/**
 * Hook for managing supplier-company visibility assignments.
 * Allows admins to assign suppliers to multiple companies.
 */
export function useSupplierVisibility(supplierId?: string) {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  // Fetch companies assigned to a supplier
  const query = useQuery<string[]>({
    queryKey: ['supplier_companies', supplierId],
    queryFn: async () => {
      if (!supplierId) return [];
      const { data, error } = await supabase
        .from('supplier_companies')
        .select('company_id')
        .eq('supplier_id', supplierId);
      
      if (error) throw error;
      return (data ?? []).map((row) => row.company_id);
    },
    enabled: !!supplierId && isAdmin,
    staleTime: 60000,
  });

  // Update supplier visibility assignments
  const updateMutation = useMutation({
    mutationFn: async ({ supplierId: id, companyIds }: { supplierId: string; companyIds: string[] }) => {
      // Delete existing assignments
      const { error: deleteError } = await supabase
        .from('supplier_companies')
        .delete()
        .eq('supplier_id', id);
      
      if (deleteError) throw deleteError;

      // Insert new assignments (if any)
      if (companyIds.length > 0) {
        const inserts = companyIds.map((companyId) => ({
          supplier_id: id,
          company_id: companyId,
        }));

        const { error: insertError } = await supabase
          .from('supplier_companies')
          .insert(inserts);

        if (insertError) throw insertError;
      }

      return { supplierId: id, companyIds };
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['supplier_companies', variables.supplierId] });
      invalidateSupplierRosterCache();
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });

  return {
    companyIds: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    updateVisibility: updateMutation.mutateAsync,
    updating: updateMutation.isPending,
  };
}
